import { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useLanguage } from "../../i18n/LanguageProvider";
import { ForumError, useForumApi } from "./forumApi";

// Choosing the public forum name, required before the first post.
export const ForumNameDialog: React.FC<{
  show: boolean;
  current: string | null;
  onClose: () => void;
  onSaved: (name: string) => void;
}> = ({ show, current, onClose, onSaved }) => {
  const { t } = useLanguage();
  const forum = useForumApi();
  const [name, setName] = useState(current ?? "");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show) {
      setName(current ?? "");
      setError(null);
    }
  }, [show, current]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const r = await forum.setName(name.trim());
      onSaved(r.forumName);
    } catch (err) {
      setError(err instanceof ForumError || err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Form onSubmit={save}>
        <Modal.Header closeButton>
          <Modal.Title>{t("forum.nameTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="fm-muted">{t("forum.nameLead")}</p>
          <p className="fm-name-lock">{t("forum.nameLockNote")}</p>
          <Form.Group>
            <Form.Label>{t("forum.nameLabel")}</Form.Label>
            <Form.Control
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={24}
              autoFocus
              isInvalid={!!error}
            />
            <Form.Text muted>{t("forum.nameHint")}</Form.Text>
            {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose}>
            {t("forum.cancel")}
          </Button>
          <Button type="submit" disabled={saving || name.trim().length < 3}>
            {t("forum.nameSave")}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
